// Invite/revoke go through Cloud Functions (functions/client-provisioning-functions.js),
// never direct Firestore writes.
import firebase from '../config/firebase.config';
import httpClient from '../config/httpClient';

async function authHeader() {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('User not authenticated');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

/**
 * @param {{ companyId, teamIds: string[], inviteeEmail, inviteeName }} payload
 */
async function sendClientInvite(payload) {
  try {
    const headers = await authHeader();
    const response = await httpClient.post('/sendClientInvite', payload, {
      headers,
    });
    if (!response?.data?.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to send client invite',
      };
    }
    return {
      success: true,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (e) {
    return {
      success: false,
      message:
        e?.response?.data?.message ||
        e?.message ||
        'Failed to send client invite',
    };
  }
}

/**
 * @param {{ companyId, clientUid, teamIds?: string[] }} payload
 * teamIds omitted revokes every team grant (and disables the login).
 */
async function revokeClientAccess(payload) {
  try {
    const headers = await authHeader();
    const response = await httpClient.post('/revokeClientAccess', payload, {
      headers,
    });
    if (!response?.data?.success) {
      return {
        success: false,
        message: response?.data?.message || 'Failed to revoke client access',
      };
    }
    return {
      success: true,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (e) {
    return {
      success: false,
      message:
        e?.response?.data?.message ||
        e?.message ||
        'Failed to revoke client access',
    };
  }
}

/** @param {{ inviteId, inviteToken }} payload */
async function acceptClientInvite(payload) {
  try {
    const fn = firebase.functions().httpsCallable('acceptClientInvite');
    const result = await fn(payload);
    return { success: true, data: result.data };
  } catch (e) {
    return {
      success: false,
      message: e?.message || 'Failed to accept invite',
    };
  }
}

/** Lists current clients granted access to a given team (admin-only view). */
async function getTeamClientAccess(companyId, teamId) {
  try {
    const snap = await firebase
      .firestore()
      .collection('companyDatabase')
      .doc(companyId)
      .collection('teamList')
      .doc(teamId)
      .collection('clientAccess')
      .get();
    return {
      success: true,
      data: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    };
  } catch (e) {
    return { success: false, message: e?.message || 'Failed to load clients' };
  }
}

export default {
  sendClientInvite,
  revokeClientAccess,
  acceptClientInvite,
  getTeamClientAccess,
};
